import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import { Chip, Paper, TableContainer, TableSortLabel, Tooltip } from "@mui/material";

import conf from "../../conf";
import { useAuth } from "../auth/AuthProvider";
import ProviderEdit from "./ProviderEdit";
import LinearLoader from "../utils/LinearLoader";
import EmptyTableRow from "../utils/Empty";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import { useSmartContent } from "../utils/SmartCell";
import { IdpConfig, useConfgOrganizationRemoveIdp, useConfgOrganizationSettings } from "../../hooks/confg/base";

type ProviderRowProps = { idp: IdpConfig; isAdmin: boolean };

const ProviderRow = (props: ProviderRowProps) => {
  const idp = props.idp;
  const { name, authority, tenantId, clientId } = idp;

  const remove = useConfgOrganizationRemoveIdp();

  const handleRemove = () => remove.mutate(name);

  const warning = (
    <>
      You are about to remove the <b>{name}</b> identity provider.
      <br />
      This action cannot be undone. Are you sure you want to continue?
    </>
  );

  return (
    <TableRow sx={conf.STYLE.LAST_CHILD_NO_BORDER}>
      <TableCell component="th" scope="row">
        <Chip size="small" label={name} />
      </TableCell>

      <TableCell align="right">{useSmartContent(authority)}</TableCell>

      <TableCell align="right">{useSmartContent(tenantId)}</TableCell>

      <TableCell align="right">{useSmartContent(clientId)}</TableCell>

      <TableCell align="right">{"**********"}</TableCell>

      <TableCell align="right">
        <ProviderEdit idp={idp} />
      </TableCell>

      <TableCell align="right">
        <ButtonWithConfirm
          size="small"
          type="remove provider"
          label="remove"
          color="error"
          variant="outlined"
          status={remove.status}
          message={warning}
          onConfirm={handleRemove}
        />
      </TableCell>
    </TableRow>
  );
};

const Providers = () => {
  const { user: self } = useAuth();

  const isAdmin = ["org.admin", "sys.admin"].includes(self.activeOrganization.role);

  const org = useConfgOrganizationSettings();

  return (
    <Paper elevation={3}>
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table aria-label="collapsible table" stickyHeader>
          <TableHead>
            <TableRow>
              {conf.COLUMNS.IDP.map((column) => (
                <TableCell key={column.key} align={column.isHeader ? "left" : "right"}>
                  <TableSortLabel>
                    {column.description ? (
                      <Tooltip title={column.description} placement="top">
                        <b>{column.label}</b>
                      </Tooltip>
                    ) : (
                      <b>{column.label}</b>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}

              {isAdmin && (
                <TableCell align="right">
                  <SettingsIcon fontSize="inherit" />
                </TableCell>
              )}

              {isAdmin && (
                <TableCell align="right">
                  <DeleteIcon fontSize="inherit" />
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          {org.isLoading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={8}>
                  <LinearLoader />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {org?.data?.data?.idp.length === 0 ? (
                <EmptyTableRow type="providers" colSpan={6} />
              ) : (
                org?.data?.data?.idp.map((provider) => <ProviderRow key={provider.name} idp={provider} isAdmin={isAdmin} />)
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default Providers;
